"use client";

import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";

export const DRAWER_TRANSITION_MS = 280;

export interface DrawerDialogProps {
  title: string;
  labelledBy: string;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Bottom sheet nativo (`<dialog>` + showModal) com animação de entrada/saída.
 *
 * Mora em `ui/` porque tem três consumidores em áreas diferentes: os filtros do
 * catálogo (onde ele nasceu), o menu "Mais" da sidebar do painel e o Top 5 das
 * jornadas. Os três importam daqui.
 */
export function DrawerDialog({ title, labelledBy, open, onClose, children }: DrawerDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const [visible, setVisible] = useState(false);

  // Sync `open` prop with native dialog open state + animation timing.
  // Opening: showModal() commits the DOM at translate-y-full; double rAF
  // flips to translate-y-0 so the browser animates the diff.
  // Closing: setVisible(false) starts the slide-down; .close() runs after
  // the transition finishes so the exit is visible.
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else if (!open && d.open) {
      setVisible(false);
      const t = window.setTimeout(() => d.close(), DRAWER_TRANSITION_MS);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  // ESC fires `cancel` on <dialog>. Intercept so we can run the exit
  // animation instead of the browser hard-closing the element.
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const onCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    d.addEventListener("cancel", onCancel);
    return () => d.removeEventListener("cancel", onCancel);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={labelledBy}
      data-visible={visible}
      className={cn(
        "m-0 max-h-none max-w-none border-0 bg-transparent p-0",
        "h-full w-full",
        "backdrop:bg-bg-dark/0 backdrop:backdrop-blur-none backdrop:transition-all backdrop:duration-300 backdrop:ease-out",
        "data-[visible=true]:backdrop:bg-bg-dark/60 data-[visible=true]:backdrop:backdrop-blur-sm",
      )}
    >
      <div className="pointer-events-none relative z-10 flex min-h-full items-end">
        <div
          className={cn(
            "pointer-events-auto flex max-h-[90vh] w-full flex-col rounded-t-lg bg-surface shadow-xl",
            "transform transition-transform ease-out will-change-transform",
            visible ? "translate-y-0" : "translate-y-full",
          )}
          style={{ transitionDuration: `${DRAWER_TRANSITION_MS}ms` }}
        >
          <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
            <h2 id={labelledBy} className="text-body-md font-semibold text-fg-strong">
              {title}
            </h2>
            <IconButton
              variant="subtle"
              onClick={onClose}
              aria-label={`Fechar ${title.toLowerCase()}`}
              className="text-fg-muted"
            >
              <X aria-hidden className="h-5 w-5" />
            </IconButton>
          </div>

          <div className="flex-1 overflow-y-auto">{children}</div>
        </div>
      </div>
      {/* Depois do painel para não receber o foco inicial do `showModal()`. */}
      <button
        type="button"
        aria-label={`Fechar ${title.toLowerCase()}`}
        tabIndex={-1}
        onClick={onClose}
        className="fixed inset-0 z-0 cursor-default bg-transparent"
      />
    </dialog>
  );
}
